/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { Text, View, ArrowRight, type TextProps, Button, Icon } from '@az/starc-ui';
import type {
  ContentStackBlogtypeGrid,
  ContentStackFeaturedContent,
  ContentStackPostReviewer,
  ContentStackBlogPostSummary,
} from '../../interface';
import styles from './CMSImageContentCard.module.scss';
import cx from 'classnames';
import { CMSImage } from '../CMSImage/CMSImage';
import { isModalLink } from '../../utils/isModalLink';
import { ModalLink } from '@/components/ModalLink/ModalLink';
import { CMSLink } from '../CMSLink/CMSLink';
import { BlogReviewer } from '@/features/blog/components/BlogReviewer/BlogReviewer';
import { useCMSRichTextModal } from '../../hooks/useCMSRichTextModal';

type Props = (
  | NonNullable<ContentStackBlogtypeGrid['blog_content']>[0]
  | (NonNullable<ContentStackFeaturedContent['feature_content']>[0] & {
      withBackground?: boolean;
      textColor?: TextProps['color'];
    })
) & {
  review?: {
    blogReviewer: Pick<ContentStackPostReviewer, 'title' | 'post_review' | 'url'> | undefined;
    date: ContentStackBlogPostSummary['reviewed_date'];
  };
  isHorizontalScrollEnabled?: boolean;
};

export function CMSImageContentCard({
  ref,
  image,
  heading,
  description,
  review,
  ...optionalProps
}: Props & { ref?: React.Ref<HTMLImageElement> }) {
  const imageLink = optionalProps.link;
  const ctaLink = 'link_cta' in optionalProps ? optionalProps.link_cta : undefined;
  const withBackground =
    'withBackground' in optionalProps ? optionalProps.withBackground : undefined;
  const linkTarget = 'target' in optionalProps ? (optionalProps.target ?? '_self') : '_self';
  const { isHorizontalScrollEnabled } = optionalProps;

  const textColor = 'textColor' in optionalProps ? optionalProps.textColor : undefined;

  const hasModalLink = isModalLink(ctaLink?.href ?? '');

  const { processedHTML, handleClick, ModalComponents } = useCMSRichTextModal(description);

  return (
    <View className={cx(styles.container, isHorizontalScrollEnabled && styles.containerScrollable)}>
      {image && (
        <View
          className={cx(
            styles.imgContainer,
            isHorizontalScrollEnabled && styles.imgContainerScrollable,
            isHorizontalScrollEnabled && !withBackground && styles.imgContainerScrollableNoPadding,
          )}
        >
          {imageLink ? (
            <CMSLink to={imageLink} target={linkTarget}>
              <CMSImage image={image} ref={ref} />
            </CMSLink>
          ) : (
            <CMSImage image={image} ref={ref} />
          )}
          {withBackground && <View className={cx(styles.imgBackground, styles.withBackground)} />}
        </View>
      )}
      <View
        direction="column"
        align="start"
        className={cx(
          styles.contentContainer,
          withBackground && styles.withBackground,
          isHorizontalScrollEnabled &&
            !withBackground &&
            styles.contentContainerScrollableNoPadding,
        )}
      >
        {heading && (
          <Text className={styles.heading} weight="bold" color={textColor}>
            {heading}
          </Text>
        )}
        {description && (
          <>
            <Text
              className={styles.description}
              attributes={{
                dangerouslySetInnerHTML: { __html: processedHTML },
                onClick: handleClick,
              }}
            >
              {null}
            </Text>
            <ModalComponents />
          </>
        )}
        {review && review.blogReviewer ? (
          <BlogReviewer blogReviewer={review.blogReviewer} date={review.date} verticalPadding />
        ) : null}
        {hasModalLink && ctaLink?.title && (
          <ModalLink
            modalId={ctaLink.href}
            className={cx(styles.linkButton, styles.modalLinkButton)}
          >
            <Text
              className={styles.linkTitle}
              fontFamily="headline"
              weight="medium"
              textCase="uppercase"
            >
              {ctaLink.title}
              {heading && <span className="sr-only"> {heading}</span>}
            </Text>
            <Icon className={styles.modalIcon} size={7} svg={ArrowRight} />
          </ModalLink>
        )}
        {!hasModalLink && ctaLink?.href && ctaLink.title && (
          <Button
            href={ctaLink.href}
            attributes={{
              target: linkTarget,
              rel: linkTarget === '_blank' ? 'noopener noreferrer' : undefined,
              'aria-label': heading ? `${ctaLink.title} — ${heading} ` : ctaLink.title,
            }}
            variant="ghost"
            endIcon={ArrowRight}
            className={styles.linkButton}
          >
            <Text className={styles.linkTitle}>
              {ctaLink.title}
              {heading && (
                <span className="sr-only">
                  {heading + (linkTarget === '_blank' ? ' opens in a new window' : '')}{' '}
                </span>
              )}
            </Text>
          </Button>
        )}
      </View>
    </View>
  );
}
